import { useEffect, useRef, useState } from "react";
import { Search, Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";

// Reusable worldwide address search with live autocomplete dropdown.
export default function AddressSearch({ onSelect, placeholder = "Search address, city or place…", idPrefix = "loc" }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const skipRef = useRef(false);

  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setShowSug(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/tools/geocode-suggest", { params: { q } });
        setSuggestions(data.suggestions || []);
        setShowSug((data.suggestions || []).length > 0);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => {
    const parts = s.label.split(",").map((p) => p.trim());
    skipRef.current = true;
    setQuery(parts.slice(0, 3).join(", "));
    setShowSug(false);
    setSuggestions([]);
    setError("");
    onSelect({ lat: s.lat, lon: s.lon, label: parts.slice(0, 2).join(", ") });
  };

  const onKeyDown = (e) => {
    if (!showSug || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setShowSug(false);
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (activeIdx >= 0 && suggestions[activeIdx]) {
      pick(suggestions[activeIdx]);
      return;
    }
    const q = query.trim();
    if (!q) return;
    setShowSug(false);
    setSearching(true);
    setError("");
    try {
      const { data } = await api.get("/tools/geocode", { params: { q } });
      const parts = (data.label || q).split(",").map((p) => p.trim());
      setQuery(parts.slice(0, 3).join(", "));
      onSelect({ lat: data.lat, lon: data.lon, label: parts.slice(0, 2).join(", ") });
    } catch (err) {
      setError(err?.response?.data?.detail || "No match found. Try a city or place name.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <form onSubmit={submit} className="relative" ref={boxRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ld-muted)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSug(true)}
            autoComplete="off"
            placeholder={placeholder}
            data-testid={`${idPrefix}-input`}
            className="w-full rounded-xl border border-[var(--ld-line)] bg-[var(--ld-bg)] py-3 pl-9 pr-3 text-sm font-bold text-[var(--ld-text)] outline-none placeholder:text-[var(--ld-muted)] focus:border-[var(--ld-lime)]"
          />
        </div>
        <button
          type="submit"
          disabled={searching}
          data-testid={`${idPrefix}-submit`}
          className="ld-btn-lime inline-flex shrink-0 items-center gap-2 rounded-xl px-5 py-3 text-sm font-black disabled:opacity-60"
        >
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>

      {showSug && suggestions.length > 0 && (
        <ul
          data-testid={`${idPrefix}-suggestions`}
          className="absolute left-0 right-0 top-full z-[1000] mt-2 max-h-72 overflow-auto rounded-xl border border-[var(--ld-line)] bg-[var(--ld-surface)] py-1 shadow-2xl shadow-black/50"
        >
          {suggestions.map((s, i) => (
            <li key={`${s.lat}-${s.lon}-${i}`}>
              <button
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => pick(s)}
                data-testid={`${idPrefix}-suggestion-${i}`}
                className={`flex w-full items-start gap-2 px-3 py-2.5 text-left text-xs font-semibold leading-snug transition-colors ${
                  activeIdx === i ? "bg-[var(--ld-lime)]/15 text-[var(--ld-text)]" : "text-[var(--ld-muted)] hover:text-[var(--ld-text)]"
                }`}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ld-lime)]" />
                <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="mt-2 text-xs font-bold text-red-400" data-testid={`${idPrefix}-error`}>
          {error}
        </p>
      )}
    </form>
  );
}
